import { type FC, useMemo } from 'react';

import {
  AssessmentOutlined,
  BoltOutlined,
  FolderOpen,
  ViewListOutlined,
} from '@mui/icons-material';
import { alpha, Drawer, List, ListItem, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router';

import { FeatureFlagName } from '@ll-platform/frontend/config/featureFlags/featureFlags';
import { activityTracker } from '@ll-platform/frontend/core/analytics/activityTracker';
import { ActivityType } from '@ll-platform/frontend/core/analytics/events';
import { useActiveUser } from '@ll-platform/frontend/core/auth/hooks/useActiveUser';
import { AccountType } from '@ll-platform/frontend/core/auth/types';
import { useAppFlags } from '@ll-platform/frontend/core/featureFlags/useAppFlags';
import { Pages } from '@ll-platform/frontend/core/router/pages';
import { useUpdateUserPreferences } from '@ll-platform/frontend/core/users/async/useUsersMutations';
import { truthy } from '@ll-platform/shared-utils/types';

type NavItem = {
  page: Pages;
  label: string;
  icon: FC;
  analyticsActivity?: ActivityType;
};

type SidebarNavigationProps = {
  isOpen: boolean;
};

export const SidebarNavigation = ({ isOpen }: SidebarNavigationProps) => {
  const location = useLocation();
  const flags = useAppFlags();
  const { activeUser } = useActiveUser();

  const updateUserPreferencesMutation = useUpdateUserPreferences({
    meta: { supressErrorToast: true },
  });

  const items = useMemo(() => {
    return (
      [
        {
          page: Pages.InternalProjects,
          label: 'Projects',
          icon: FolderOpen,
          analyticsActivity: ActivityType.DashboardClickedProjectView,
        },
        activeUser.accountType !== AccountType.Freelancer && {
          page: Pages.InternalContracts,
          label: 'Contracts',
          icon: BoltOutlined,
          analyticsActivity: ActivityType.DashboardClickedContractsView,
        },
        activeUser.accountType !== AccountType.Freelancer &&
          flags[FeatureFlagName.CreativeProposals] && {
            page: Pages.InternalProposals,
            label: 'Creative Proposal (legacy)',
            icon: ViewListOutlined,
            analyticsActivity:
              ActivityType.DashboardClickedCreativeProposalsView,
          },
        activeUser.accountType !== AccountType.Freelancer &&
          flags[FeatureFlagName.AdminReports] && {
            page: Pages.InternalAdminReports,
            label: 'Admin Reports',
            icon: AssessmentOutlined,
          },
      ] satisfies (NavItem | boolean)[]
    ).filter(truthy);
  }, [flags, activeUser.accountType]);

  const width = 276;

  return (
    <Drawer
      variant="permanent"
      open={isOpen}
      component="aside"
      sx={(theme) => {
        // Based on https://mui.com/material-ui/react-drawer/#mini-variant-drawer
        const openedMixin = {
          width,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
        };
        const closedMixin = {
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          overflowX: 'hidden',
          width: `calc(${theme.spacing(8)} + 1px)`,
        };

        return {
          width,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          ...(isOpen
            ? {
                '&, & .MuiDrawer-paper': openedMixin,
              }
            : { '&, & .MuiDrawer-paper': closedMixin }),
          ':hover': {
            '& .MuiDrawer-paper': openedMixin,
          },
        };
      }}
    >
      <List
        component="aside"
        sx={(theme) => ({
          background: 'inherit',
          py: 11,
          position: 'relative',
          ...(isOpen
            ? {
                px: 2,
                transition: theme.transitions.create('padding', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }
            : {
                px: 1.5,
                transition: theme.transitions.create('padding', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
              }),
          '::after': {
            // cover up protruding labels
            content: '""',
            position: 'absolute',
            insetBlock: 0,
            right: 0,
            width: theme.spacing(1.5),
            zIndex: 1,
            background: 'inherit',
          },
        })}
      >
        {items.map((item) => {
          const Icon = item.icon;

          return (
            <ListItem
              key={item.page}
              component={Link}
              to={item.page}
              onClick={() => {
                updateUserPreferencesMutation.mutateAsync({
                  id: activeUser._id,
                  preferences: {
                    internalDashboardDefaultPage: item.page,
                  },
                });
                if (item.analyticsActivity) {
                  activityTracker.log(item.analyticsActivity);
                }
              }}
              sx={{
                px: 1,
                py: 1,
                borderRadius: 2,
                display: 'flex',
                gap: 1.5,
                transition:
                  'opacity 0.15s ease-in-out, background 0.15s ease-in-out',
                ...(item.page === location?.pathname
                  ? {
                      background: (theme) =>
                        alpha(
                          theme.palette.primary.main,
                          theme.palette.action.selectedOpacity,
                        ),
                    }
                  : {
                      opacity: 0.6,
                      ':hover': {
                        background: (theme) =>
                          alpha(
                            theme.palette.primary.main,
                            theme.palette.action.hoverOpacity,
                          ),
                        opacity: 0.8,
                      },
                    }),
                ':active': {
                  background: (theme) =>
                    alpha(
                      theme.palette.primary.main,
                      theme.palette.action.activatedOpacity,
                    ),
                },
              }}
            >
              <Icon
                sx={{
                  color: (theme) => theme.palette.text.primary,
                }}
              />
              <Typography variant="body1" fontWeight={700} color="text.primary">
                {item.label}
              </Typography>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};
